import * as React from 'react'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import {
    formatFloat,
    formatInt,
    useBasicNetworkStats,
    useStreamCount,
} from '../../../utils/network'
import { Section, SectionContent, SectionDescription, SectionTitle } from '../../Section'
import Swipeable from '../../shared/Swipeable'

const MetricCount = 4

export function OnChainMetrics() {
    const { operatorCount, totalStake, apy } = useBasicNetworkStats()

    const streamCount = useStreamCount()

    const [offset, setOffset] = React.useState(0)

    function prev() {
        setOffset((c) => Math.max(0, c - 1))
    }

    function next() {
        setOffset((c) => Math.min(MetricCount - 1, c + 1))
    }

    return (
        <OnChainMetricsRoot>
            <SectionContent>
                <SectionTitleWrap>
                    <SectionTitle>On-chain DATA&nbsp;metrics</SectionTitle>
                    <Nav>
                        <NavButton disabled={offset === 0} onClick={prev}>
                            <svg
                                width="12"
                                height="20"
                                viewBox="0 0 12 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10.0877 1.51562L1.59766 10.0056L10.0829 18.4909"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </NavButton>
                        <NavButton disabled={offset === MetricCount - 1} onClick={next}>
                            <svg
                                width="12"
                                height="20"
                                viewBox="0 0 12 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1.91406 1.51562L10.4041 10.0056L1.91879 18.4909"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </NavButton>
                    </Nav>
                </SectionTitleWrap>
                <SectionDescription>
                    Explore our Dune dashboard to monitor DATA staked, protocol fees, and a
                    comprehensive range of on-chain usage metrics.
                </SectionDescription>
                <MetricTiles onSwipedLeft={next} onSwipedRight={prev}>
                    <MetricTile
                        style={{
                            transform: `translateX(calc(-${offset * 100}% - ${offset * 28}px))`,
                        }}
                    >
                        <MetricTileWedge />
                        <div>
                            <MetricTileValue>
                                {formatFloat((totalStake || 0) / 10 ** 6)}M
                            </MetricTileValue>
                            <MetricTileLabel>$DATA</MetricTileLabel>
                        </div>
                    </MetricTile>
                    <MetricTile
                        style={{
                            transform: `translateX(calc(-${offset * 100}% - ${offset * 28}px))`,
                        }}
                    >
                        <MetricTileWedge />
                        <div>
                            <MetricTileValue>{streamCount ?? 0}</MetricTileValue>
                            <MetricTileLabel>Streams</MetricTileLabel>
                        </div>
                    </MetricTile>
                    <MetricTile
                        style={{
                            transform: `translateX(calc(-${offset * 100}% - ${offset * 28}px))`,
                        }}
                    >
                        <MetricTileWedge />
                        <div>
                            <MetricTileValue>{formatInt(operatorCount || 0)}</MetricTileValue>
                            <MetricTileLabel>Operators</MetricTileLabel>
                        </div>
                    </MetricTile>
                    <MetricTile
                        style={{
                            transform: `translateX(calc(-${offset * 100}% - ${offset * 28}px))`,
                        }}
                    >
                        <MetricTileWedge />
                        <div>
                            <MetricTileValue>{formatFloat(Number(apy || 0))}%</MetricTileValue>
                            <MetricTileLabel>APY</MetricTileLabel>
                        </div>
                    </MetricTile>
                    {[...Array(DisabledTileCount)].map((_, index) => (
                        <MetricTile
                            key={`Disabled-tile-${index}`}
                            style={{
                                borderWidth: 0,
                                opacity: `${
                                    (0.3 * (DisabledTileCount - index)) / DisabledTileCount
                                }`,
                                transform: `translateX(calc(-${offset * 100}% - ${offset * 28}px))`,
                            }}
                        />
                    ))}
                </MetricTiles>
                <ExternalLink
                    href="https://dune.com/streamr/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <ExternalLinkLabel>Check out Streamr dune dashboard</ExternalLinkLabel>
                    <ExternalLinkIcon>
                        <svg
                            width="27"
                            height="27"
                            viewBox="0 0 27 27"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.6562 9.22852C10.6562 8.89197 10.9291 8.61914 11.2656 8.61914H17.7656C18.1022 8.61914 18.375 8.89197 18.375 9.22852V15.7285C18.375 16.0651 18.1022 16.3379 17.7656 16.3379C17.4291 16.3379 17.1563 16.0651 17.1563 15.7285V9.83789H11.2656C10.9291 9.83789 10.6562 9.56506 10.6562 9.22852Z"
                                fill="white"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M18.1965 8.79762C18.4345 9.0356 18.4345 9.42143 18.1965 9.65941L9.25902 18.5969C9.02104 18.8349 8.63521 18.8349 8.39723 18.5969C8.15926 18.3589 8.15926 17.9731 8.39723 17.7351L17.3347 8.79762C17.5727 8.55965 17.9585 8.55965 18.1965 8.79762Z"
                                fill="white"
                            />
                        </svg>
                    </ExternalLinkIcon>
                </ExternalLink>
            </SectionContent>
        </OnChainMetricsRoot>
    )
}

const NavButton = styled.button`
    --arrowScale: 0.8;
    --buttonSize: 40px;

    appearance: none;
    background: none;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: var(--buttonSize);
    height: var(--buttonSize);
    border: 2px solid #ffffff;
    border-radius: 50%;
    box-sizing: border-box;

    svg {
        transform: translateX(-1px) scale(var(--arrowScale));
    }

    & + & svg {
        transform: translateX(1px) scale(var(--arrowScale));
    }

    &:active {
        transform: translateY(1px);
    }

    &:disabled {
        opacity: 0.5;
        transform: none;
    }

    @media ${MqTablet} {
        --arrowScale: 1;
        --buttonSize: 48px;
    }
`

const Nav = styled.div`
    display: flex;
    gap: 16px;
    width: 112px;
`

const SectionTitleWrap = styled.div`
    align-items: center;
    display: flex;
    margin: 0 0 var(--SectionTitleMarginBottom, 32px);

    ${SectionTitle} {
        margin: 0;
        flex-grow: 1;
    }

    @media ${MqTablet} {
        margin-bottom: var(--SectionTitleMarginBottomTablet, 48px);
    }

    @media ${MqDesktop} {
        margin-bottom: var(
            --SectionTitleMarginBottomDesktop,
            var(--SectionTitleMarginBottomTablet, 48px)
        );
    }
`

const ExternalLinkLabel = styled.div`
    flex: 1;
    min-width: 0;
`

const ExternalLinkIcon = styled.div`
    flex: 0;
    transform: translateX(8px);

    @media ${MqTablet} {
        transform: none;
    }
`

const ExternalLink = styled.a`
    align-items: center;
    display: flex;
    gap: 6px;
    line-height: 1.5;
    margin-top: 40px;
    max-width: 100%;
    min-width: 0;
    text-decoration: none !important;
    width: max-content;

    svg {
        display: block;
    }

    @media ${MqTablet} {
        margin-top: 64px;
    }
`

const DisabledTileCount = 6

const MetricTileLabel = styled.div`
    font-weight: 500;
    font-size: min(20px, 5vw);
    line-height: 1.5;
    opacity: 0.5;

    @media ${MqDesktop} {
        font-size: 24px;
    }
`

const MetricTileValue = styled.div`
    font-weight: 600;
    font-size: min(40px, 8vw);
    line-height: 1.3;

    @media ${MqDesktop} {
        font-size: 60px;
    }
`

const MetricTileWedge = styled.div`
    flex-grow: 1;
`

const MetricTile = styled.div`
    backdrop-filter: blur(10px);
    background: linear-gradient(180deg, rgba(250, 250, 250, 0.11) 0%, rgba(250, 250, 250, 0) 100%);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 320px;
    padding: 32px;
    width: 288px;
    max-width: 60vw;
    max-height: 80vw;
    transition: 300ms ease-in-out transform;
    user-select: none;

    @media ${MqDesktop} {
        width: 368px;
        height: 432px;
        padding: 48px;
    }
`

const MetricTiles = styled(Swipeable)`
    gap: 28px;
    display: flex;
    margin-top: 40px;
    width: max-content;

    @media ${MqTablet} {
        margin-top: 64px;
    }
`

const OnChainMetricsRoot = styled(Section)`
    --SectionTitleMarginBottomDesktop: 18px;

    background: #130c30;
    background-image: radial-gradient(
            circle at 92% 28%,
            rgba(87, 28, 173, 0.4) 0,
            transparent 700px
        ),
        radial-gradient(circle at 70% 60%, rgba(87, 28, 173, 0.2) 0, transparent 350px);
    color: #ffffff;
    padding-top: var(--defaultPadding) !important;

    ${SectionDescription} {
        width: 680px;
    }
`
